import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import debounce from 'lodash/debounce';

import { FilterRangeSliderProps } from '@app/components/molecules/filter-range-slider';
import { ALLOWED_FUNCTIONAL_KEYS } from '@app/consts/keys.consts';

export const useRangeSlider = ({
    min,
    max,
    step = 1,
    selectedRange,
    onChange,
}: FilterRangeSliderProps) => {
    const [localRange, setLocalRange] = useState<[number, number]>(selectedRange || [min, max]);
    const [inputValues, setInputValues] = useState<[string, string]>([
        String(localRange[0]),
        String(localRange[1]),
    ]);
    const isInteractingRef = useRef(false);
    const timeoutRef = useRef<NodeJS.Timeout>();

    const ensureMinStep = useCallback(
        (range: [number, number], changingIndex: 0 | 1): [number, number] => {
            let [newMin, newMax] = range;

            if (newMax - newMin < step) {
                if (changingIndex === 0) newMax = newMin + step;
                else newMin = newMax - step;
            }

            return [newMin, newMax];
        },
        [step],
    );

    useEffect(() => {
        if (!isInteractingRef.current && selectedRange) {
            setLocalRange(selectedRange);
        }
    }, [selectedRange]);

    useEffect(() => {
        setInputValues([String(localRange[0]), String(localRange[1])]);
    }, [localRange]);

    const handleValueChange = useCallback(
        (details: { value: number[] }) => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            isInteractingRef.current = true;

            let [newMin, newMax] = details.value as [number, number];
            const changingIndex = newMin === localRange[0] ? 0 : 1;
            [newMin, newMax] = ensureMinStep([newMin, newMax], changingIndex);

            setLocalRange([newMin, newMax]);
        },
        [localRange, ensureMinStep],
    );

    const debouncedOnChange = useMemo(
        () =>
            debounce((newRange: [number, number]) => {
                const hasChanged =
                    newRange[0] !== selectedRange?.[0] || newRange[1] !== selectedRange?.[1];

                if (hasChanged) {
                    onChange(newRange);
                }

                timeoutRef.current = setTimeout(() => {
                    // Delay resetting interaction flag to prevent immediate external sync
                    isInteractingRef.current = false;
                }, 200);
            }, 300),
        [onChange, selectedRange],
    );

    const handleValueChangeEnd = useCallback(
        (details: { value: number[] }) => {
            const newRange = details.value as [number, number];
            debouncedOnChange(newRange);
        },
        [debouncedOnChange],
    );

    const handleInputBlurOrEnter = useCallback(
        (index: 0 | 1, value: string) => {
            let sanitized = value.replace(/[^0-9]/g, '');

            if (sanitized.startsWith('0')) {
                sanitized = '';
            }

            const parsed = sanitized ? Number(sanitized) : index === 0 ? min : max;

            setLocalRange(prev => {
                let [newMin, newMax] = prev;

                if (index === 0) {
                    newMin = parsed;
                } else {
                    newMax = parsed;
                }

                [newMin, newMax] = ensureMinStep([newMin, newMax], index);

                newMin = Math.max(min, Math.min(newMin, max));
                newMax = Math.max(min, Math.min(newMax, max));

                const hasChanged = newMin !== prev[0] || newMax !== prev[1];
                if (hasChanged) {
                    onChange([newMin, newMax]);
                    return [newMin, newMax];
                } else {
                    setInputValues([String(newMin), String(newMax)]);
                    return prev;
                }
            });
        },
        [ensureMinStep, min, max, onChange],
    );

    const handleInputChange = useCallback((index: 0 | 1, value: string) => {
        if (value.startsWith('0')) {
            value = '';
        }

        setInputValues(prev => {
            const newInputs = [...prev] as [string, string];
            newInputs[index] = value;

            return newInputs;
        });
    }, []);

    const handleInputKeyDown = useCallback(
        (index: 0 | 1, e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                handleInputBlurOrEnter(index, e.currentTarget.value);
                return;
            }

            if (!ALLOWED_FUNCTIONAL_KEYS.includes(e.key) && !/^[0-9]$/.test(e.key)) {
                e.preventDefault();
            }
        },
        [handleInputBlurOrEnter],
    );

    const getInputProps = useCallback(
        (index: 0 | 1) => ({
            type: 'number' as const,
            inputMode: 'numeric' as const,
            pe: 6,
            min,
            max,
            step,
            onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                handleInputChange(index, e.target.value),
            onBlur: (e: React.FocusEvent<HTMLInputElement>) =>
                handleInputBlurOrEnter(index, e.target.value),
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => handleInputKeyDown(index, e),
            css: {
                MozAppearance: 'textfield',
            },
        }),
        [min, max, step, handleInputChange, handleInputBlurOrEnter, handleInputKeyDown],
    );

    return {
        localRange,
        inputValues,
        handleValueChange,
        handleValueChangeEnd,
        getInputProps,
    };
};
